import { Stack } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

export type QuestCalendarFullViewProps = {
    selectedDate: dayjs.Dayjs;
    onDateSelected: (date: dayjs.Dayjs) => void;
    variant?: 'page' | 'inline';
};

export const QuestCalendarFullView: FC<QuestCalendarFullViewProps> = ({ selectedDate, onDateSelected, variant = 'page' }) => {
    const { i18n } = useTranslation();
    const handleChange = (newValue: dayjs.Dayjs | null) => {
        if (newValue) onDateSelected(newValue);
    };

    return (
        <Stack
            sx={variant === 'page' ? {
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                WebkitOverflowScrolling: 'touch',
                alignItems: 'center',
                paddingTop: '8px',
                backgroundColor: 'var(--tgui--secondary_bg_color)',
            } : {
                alignItems: 'center',
            }}
        >
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={i18n.language}>
                <DateCalendar
                    key={i18n.language}
                    value={selectedDate.locale(i18n.language)}
                    onChange={handleChange}
                    sx={{
                        width: '100%',
                        maxWidth: '480px',
                        height: 'auto !important',
                        maxHeight: 'none !important',
                        overflow: 'visible !important',
                        backgroundColor: 'transparent',
                        '& .MuiDayCalendar-slideTransition': {
                            minHeight: '288px',
                        },
                        '& .MuiYearCalendar-root': {
                            maxHeight: 'min(60vh, 420px)',
                        },
                        '& .MuiYearCalendar-button': {
                            borderRadius: '10px',
                            border: '2px solid transparent',
                            boxSizing: 'border-box',
                            color: 'var(--tg-theme-text-color)',
                            '&.Mui-selected': {
                                backgroundColor: 'transparent',
                                color: 'var(--tg-theme-text-color)',
                                fontWeight: 700,
                                border: '2px solid var(--tgui--button_color)',
                                '&:hover, &:focus': {
                                    backgroundColor: 'color-mix(in srgb, var(--tgui--button_color) 12%, transparent)',
                                },
                            },
                            '&:hover': {
                                backgroundColor: 'var(--tg-theme-secondary-bg-color)',
                            },
                        },
                        '& .MuiPickersCalendarHeader-root': {
                            color: 'var(--tg-theme-text-color)',
                            paddingLeft: '16px',
                            paddingRight: '16px',
                        },
                        '& .MuiPickersCalendarHeader-label': {
                            color: 'var(--tg-theme-text-color)',
                            fontSize: '1.2rem',
                        },
                        '& .MuiDayCalendar-header': {
                            justifyContent: 'space-around',
                        },
                        '& .MuiDayCalendar-weekContainer': {
                            justifyContent: 'space-around',
                        },
                        '& .MuiDayCalendar-weekDayLabel': {
                            color: 'var(--tg-theme-hint-color)',
                            fontSize: '0.9rem',
                        },
                        '& .MuiPickersDay-root': {
                            color: 'var(--tg-theme-text-color)',
                            width: '44px',
                            height: '44px',
                            fontSize: '1.05rem',
                            border: '2px solid transparent',
                            borderRadius: '10px',
                            boxSizing: 'border-box',
                            position: 'relative',
                            '&.MuiPickersDay-today': {
                                border: '2px solid transparent',
                                fontWeight: 700,
                                color: 'var(--tgui--button_color)',
                                '&::after': {
                                    content: '""',
                                    position: 'absolute',
                                    bottom: '5px',
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    width: '4px',
                                    height: '4px',
                                    borderRadius: '50%',
                                    backgroundColor: 'var(--tgui--button_color)',
                                },
                            },
                            '&.Mui-selected': {
                                backgroundColor: 'transparent',
                                color: 'var(--tg-theme-text-color)',
                                fontWeight: 700,
                                border: '2px solid var(--tgui--button_color)',
                                '&::after': {
                                    display: 'none',
                                },
                                '&:hover': {
                                    backgroundColor: 'color-mix(in srgb, var(--tgui--button_color) 12%, transparent)',
                                },
                                '&:focus': {
                                    backgroundColor: 'transparent',
                                },
                            },
                            '&:hover': {
                                backgroundColor: 'var(--tg-theme-secondary-bg-color)',
                            },
                        },
                        '& .MuiSvgIcon-root': {
                            color: 'var(--tg-theme-text-color)',
                        },
                    }}
                />
            </LocalizationProvider>
        </Stack>
    );
};
